import { useEffect, useRef } from "react";

type Props = {
    levelRef: React.MutableRefObject<number>;
    isListening: boolean;
    isSpeaking: boolean;
    size?: number;
};

const NUM_POINTS = 128;
const FADE_MS = 500;

type RGB = [number, number, number];

// Color palettes for each mode — GN CVI colors
const PALETTES = {
    idle: {
        glowInner: [156, 163, 175] as RGB, // gray-400
        glowMid:   [107, 114, 128] as RGB, // gray-500
        glowOuter: [75, 85, 99]    as RGB, // gray-600
        blobLight: [209, 213, 219] as RGB, // gray-300
        blobMid:   [107, 114, 128] as RGB, // gray-500
        blobDark:  [55, 65, 81]    as RGB, // gray-700
    },
    listening: {
        glowInner: [255, 185, 80]  as RGB, // bright orange highlight
        glowMid:   [234, 114, 0]   as RGB, // GN Orange #EA7200
        glowOuter: [160, 78, 0]    as RGB, // dark orange
        blobLight: [255, 200, 110] as RGB, // light orange
        blobMid:   [234, 114, 0]   as RGB, // GN Orange
        blobDark:  [150, 70, 0]    as RGB, // dark orange
    },
    speaking: {
        glowInner: [225, 208, 160] as RGB, // light gold highlight
        glowMid:   [189, 155, 96]  as RGB, // GN Gold #BD9B60
        glowOuter: [130, 105, 58]  as RGB, // dark gold
        blobLight: [235, 218, 168] as RGB, // light gold
        blobMid:   [189, 155, 96]  as RGB, // GN Gold
        blobDark:  [120, 95, 50]   as RGB, // dark gold
    },
};

type PaletteColors = typeof PALETTES.idle;

function lerpRGB(a: RGB, b: RGB, t: number): RGB {
    return [
        Math.round(a[0] + (b[0] - a[0]) * t),
        Math.round(a[1] + (b[1] - a[1]) * t),
        Math.round(a[2] + (b[2] - a[2]) * t),
    ];
}

function blendPalettes(from: PaletteColors, to: PaletteColors, t: number): PaletteColors {
    // smoothstep easing
    const e = t * t * (3 - 2 * t);
    return {
        glowInner: lerpRGB(from.glowInner, to.glowInner, e),
        glowMid:   lerpRGB(from.glowMid,   to.glowMid,   e),
        glowOuter: lerpRGB(from.glowOuter, to.glowOuter, e),
        blobLight: lerpRGB(from.blobLight, to.blobLight, e),
        blobMid:   lerpRGB(from.blobMid,   to.blobMid,   e),
        blobDark:  lerpRGB(from.blobDark,  to.blobDark,  e),
    };
}

function rgbaStr([r, g, b]: RGB, a: number): string {
    return `rgba(${r},${g},${b},${a.toFixed(3)})`;
}

// Layered sine wave deformation for organic blob shape
function blobDeform(angle: number, t: number, level: number): number {
    return (
        Math.sin(angle * 2 + t * 0.9)  * (0.040 + level * 0.100) +
        Math.sin(angle * 3 - t * 1.4)  * (0.030 + level * 0.070) +
        Math.sin(angle * 5 + t * 2.1)  * (0.020 + level * 0.045) +
        Math.sin(angle * 7 - t * 1.7)  * (0.012 + level * 0.025) +
        Math.cos(angle * 4 + t * 1.2)  * (0.015 + level * 0.040)
    );
}

export default function VoiceOrb({ levelRef, isListening, isSpeaking, size = 220 }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const frameRef = useRef<number>(0);

    const stateRef = useRef({
        isListening,
        isSpeaking,
        smoothedLevel: 0,
        time: 0,
        lastTs: 0,
        // Color transition state
        fromPalette: { ...PALETTES.idle } as PaletteColors,
        toPalette:   { ...PALETTES.idle } as PaletteColors,
        transitionT: 1.0,
        currentMode: "idle" as "idle" | "listening" | "speaking",
    });

    useEffect(() => {
        stateRef.current.isListening = isListening;
        stateRef.current.isSpeaking = isSpeaking;
    }, [isListening, isSpeaking]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d")!;
        const dpr = window.devicePixelRatio || 1;
        const S = size * dpr;
        canvas.width = S;
        canvas.height = S;

        const cx = S / 2;
        const cy = S / 2;
        const BASE_R = S * 0.28;

        const draw = (ts: number) => {
            const s = stateRef.current;
            const dt = s.lastTs === 0 ? 16 : Math.min(ts - s.lastTs, 50);
            s.lastTs = ts;

            // ── Color transition ──────────────────────────────────────────────
            const targetMode = s.isListening ? "listening" : s.isSpeaking ? "speaking" : "idle";
            if (targetMode !== s.currentMode) {
                // Capture current blend as the new "from" so mid-transition changes are smooth
                s.fromPalette = blendPalettes(s.fromPalette, s.toPalette, Math.min(1, s.transitionT));
                s.toPalette = { ...PALETTES[targetMode] };
                s.transitionT = 0;
                s.currentMode = targetMode;
            }
            s.transitionT = Math.min(1, s.transitionT + dt / FADE_MS);
            const palette = blendPalettes(s.fromPalette, s.toPalette, s.transitionT);

            // ── Audio level smoothing ─────────────────────────────────────────
            let targetLevel: number;
            if (s.isListening) {
                targetLevel = levelRef.current;
            } else if (s.isSpeaking) {
                // Subtle, smooth simulated activity when AI is replying
                targetLevel = 0.35 + Math.sin(ts / 600) * 0.08 + Math.sin(ts / 350) * 0.04;
            } else {
                targetLevel = 0;
            }
            const lerpFactor = targetLevel > s.smoothedLevel ? 0.10 : 0.05;
            s.smoothedLevel += (targetLevel - s.smoothedLevel) * lerpFactor;
            const level = s.smoothedLevel;

            // ── Animation speed ───────────────────────────────────────────────
            // idle: very slow and peaceful
            // listening quiet: gentle, a bit more alive
            // user talking: dynamic, speed scales with loudness
            // AI speaking: lively, driven by simulated level
            let baseSpeed: number;
            if (!s.isListening && !s.isSpeaking) {
                baseSpeed = 0.18;                  // idle: very slow
            } else if (s.isListening) {
                baseSpeed = 0.45 + level * 4.5;   // quiet: 0.45; talking: up to ~5
            } else {
                baseSpeed = 0.55 + level * 3.0;   // speaking: driven by simulated level
            }
            s.time += (dt / 1000) * baseSpeed;
            const t = s.time;

            // ── Deformation level for blob ────────────────────────────────────
            // Idle gets near-zero deform; listening/speaking get full level-driven deform
            const deformLevel = (!s.isListening && !s.isSpeaking) ? level * 0.25 : level;

            // Breathing rate and amplitude scale with activity
            const breatheRate = (s.isListening || s.isSpeaking) ? (1.2 + level * 2.0) : 0.35;
            const breatheAmp  = (s.isListening || s.isSpeaking) ? 0.025 : 0.012;
            const breathe = Math.sin(t * breatheRate) * breatheAmp;

            ctx.clearRect(0, 0, S, S);

            // ── Outer soft glow ───────────────────────────────────────────────
            const glowR = Math.min(BASE_R * (2.0 + deformLevel * 0.9), S * 0.46);
            const isActive = s.isListening || s.isSpeaking;
            const glowAlpha = (isActive ? 0.28 : 0.10) + deformLevel * 0.38;
            const glowGrad = ctx.createRadialGradient(cx, cy, BASE_R * 0.3, cx, cy, glowR);
            glowGrad.addColorStop(0,    rgbaStr(palette.glowInner, glowAlpha));
            glowGrad.addColorStop(0.40, rgbaStr(palette.glowMid,   glowAlpha * 0.45));
            glowGrad.addColorStop(0.72, rgbaStr(palette.glowOuter, glowAlpha * 0.10));
            glowGrad.addColorStop(1,    rgbaStr(palette.glowOuter, 0));
            ctx.beginPath();
            ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
            ctx.fillStyle = glowGrad;
            ctx.fill();

            // ── Organic blob ──────────────────────────────────────────────────
            ctx.beginPath();
            for (let i = 0; i <= NUM_POINTS; i++) {
                const angle = (i / NUM_POINTS) * Math.PI * 2;
                const r = BASE_R * (1 + breathe + blobDeform(angle, t, deformLevel));
                const x = cx + Math.cos(angle) * r;
                const y = cy + Math.sin(angle) * r;
                i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
            }
            ctx.closePath();

            const blobAlpha = isActive ? 0.97 : 0.88;
            // For listening, brighten slightly with audio level
            const lightBoost = s.isListening ? lerpRGB(palette.blobLight, [255, 230, 160], deformLevel * 0.5) : palette.blobLight;
            const fillGrad = ctx.createRadialGradient(
                cx - BASE_R * 0.25, cy - BASE_R * 0.30, 0,
                cx, cy, BASE_R * 1.15
            );
            fillGrad.addColorStop(0,    rgbaStr(lightBoost,      blobAlpha));
            fillGrad.addColorStop(0.55, rgbaStr(palette.blobMid,  blobAlpha));
            fillGrad.addColorStop(1,    rgbaStr(palette.blobDark, blobAlpha));
            ctx.fillStyle = fillGrad;
            ctx.fill();

            // ── Specular highlight ────────────────────────────────────────────
            const specGrad = ctx.createRadialGradient(
                cx - BASE_R * 0.32, cy - BASE_R * 0.38, 0,
                cx - BASE_R * 0.10, cy - BASE_R * 0.10, BASE_R * 0.75
            );
            specGrad.addColorStop(0, "rgba(255, 255, 255, 0.28)");
            specGrad.addColorStop(0.5, "rgba(255, 255, 255, 0.06)");
            specGrad.addColorStop(1, "rgba(255, 255, 255, 0)");
            ctx.beginPath();
            ctx.arc(cx, cy, BASE_R * 1.15, 0, Math.PI * 2);
            ctx.fillStyle = specGrad;
            ctx.fill();

            frameRef.current = requestAnimationFrame(draw);
        };

        frameRef.current = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(frameRef.current);
    }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <canvas
            ref={canvasRef}
            style={{ width: size, height: size }}
            aria-hidden="true"
        />
    );
}
